/**
 * Fortified Mind — Google Analytics 4 Event Helpers
 * All events are no-ops when GA is not configured.
 */

const GA_ID = import.meta.env?.VITE_GA_MEASUREMENT_ID;

const isGAReady = () =>
  GA_ID &&
  GA_ID !== 'your-google-analytics-id-here' &&
  typeof window.gtag === 'function';

/** Generic event sender */
export const trackEvent = (eventName, params = {}) => {
  if (!isGAReady()) return;
  window.gtag('event', eventName, params);
};

// ── Bible Reader ─────────────────────────────────────────────────────────────

export const trackBibleChapterNavigation = (book, chapter, version) => {
  trackEvent('bible_chapter_view', {
    event_category: 'Bible Reader',
    book_name: book,
    chapter_number: chapter,
    bible_version: version,
  });
};

export const trackBibleBookChange = (book, version) => {
  trackEvent('bible_book_change', {
    event_category: 'Bible Reader',
    book_name: book,
    bible_version: version,
  });
};

export const trackBibleVersionChange = (version) => {
  trackEvent('bible_version_change', {
    event_category: 'Bible Reader',
    bible_version: version,
  });
};

// ── Verse Library ────────────────────────────────────────────────────────────

export const trackVerseAdded = (reference, version) => {
  trackEvent('verse_added', {
    event_category: 'Verse Library',
    verse_reference: reference,
    bible_version: version,
  });
};

export const trackVerseDeleted = () => {
  trackEvent('verse_deleted', { event_category: 'Verse Library' });
};

export const trackBulkVerseImport = (count) => {
  trackEvent('bulk_verse_import', {
    event_category: 'Verse Library',
    verse_count: count,
  });
};

// ── Daily Review ─────────────────────────────────────────────────────────────

export const trackDailyReviewStarted = (verseCount) => {
  trackEvent('daily_review_started', {
    event_category: 'Daily Review',
    verse_count: verseCount,
  });
};

export const trackDailyReviewCompleted = (verseCount, durationSeconds) => {
  trackEvent('daily_review_completed', {
    event_category: 'Daily Review',
    verse_count: verseCount,
    duration_seconds: durationSeconds,
  });
};

// ── Memorization Practice ────────────────────────────────────────────────────

export const trackPracticeSessionStarted = (mode, verseCount) => {
  trackEvent('practice_session_started', {
    event_category: 'Memorization Practice',
    practice_mode: mode,
    verse_count: verseCount,
  });
};

export const trackPracticeSessionCompleted = (mode, accuracy, verseCount) => {
  trackEvent('practice_session_completed', {
    event_category: 'Memorization Practice',
    practice_mode: mode,
    accuracy_percent: Math.round(accuracy),
    verse_count: verseCount,
  });
};

// ── Reading Plans ────────────────────────────────────────────────────────────

export const trackReadingPlanEnrolled = (planName) => {
  trackEvent('reading_plan_enrolled', {
    event_category: 'Reading Plans',
    plan_name: planName,
  });
};

export const trackReadingPlanProgress = (planName, dayNumber) => {
  trackEvent('reading_plan_progress', {
    event_category: 'Reading Plans',
    plan_name: planName,
    day_number: dayNumber,
  });
};

export const trackReadingPlanCompleted = (planName) => {
  trackEvent('reading_plan_completed', {
    event_category: 'Reading Plans',
    plan_name: planName,
  });
};

// ── Study Notes ──────────────────────────────────────────────────────────────

export const trackStudyNoteCreated = () => {
  trackEvent('study_note_created', { event_category: 'Study Notes' });
};

export const trackStudyNoteShared = (slug) => {
  trackEvent('study_note_shared', {
    event_category: 'Study Notes',
    note_slug: slug,
  });
};

// ── Auth / Conversion ────────────────────────────────────────────────────────

export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', {
    event_category: 'Auth',
    method,
  });
};

export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    event_category: 'Auth',
    method,
  });
};

// ── Blog ─────────────────────────────────────────────────────────────────────

export const trackBlogPostView = (slug, title) => {
  trackEvent('blog_post_view', {
    event_category: 'Blog',
    post_slug: slug,
    post_title: title,
  });
};

export const trackNewsletterSubscribe = (source) => {
  trackEvent('newsletter_subscribe', {
    event_category: 'Blog',
    subscribe_source: source,
  });
};

// ── Verse Study Actions ───────────────────────────────────────────────────────

export const trackVerseActionOpen = (actionType, book, chapter, verse, version) => {
  trackEvent('verse_action_open', {
    event_category: 'Verse Study Actions',
    action_type: actionType, // 'egw' | 'commentaries' | 'tsk' | 'sda' | 'interlinear' | 'reflect'
    book_name: book,
    chapter_number: chapter,
    verse_number: verse,
    bible_version: version,
  });
};

export const trackEGWVerseAction = (book, chapter, verse, version) => {
  trackEvent('egw_verse_action', {
    event_category: 'Verse Study Actions',
    feature: 'EGW',
    book_name: book,
    chapter_number: chapter,
    verse_number: verse,
    bible_version: version,
  });
};

export const trackCommentariesVerseAction = (book, chapter, verse, version) => {
  trackEvent('commentaries_verse_action', {
    event_category: 'Verse Study Actions',
    feature: 'Commentaries',
    book_name: book,
    chapter_number: chapter,
    verse_number: verse,
    bible_version: version,
  });
};

export const trackTSKVerseAction = (book, chapter, verse, version) => {
  trackEvent('tsk_verse_action', {
    event_category: 'Verse Study Actions',
    feature: 'TSK',
    book_name: book,
    chapter_number: chapter,
    verse_number: verse,
    bible_version: version,
  });
};
